import React, { useState, useEffect } from "react";
import { Maximize2, Upload, FileCode, Image as ImageIcon, Sparkles } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { getUploadUrl } from "@/lib/utils";

interface POSVGMarquetteProps {
  printLayouts?: any[];
  sheetWidth?: number | string | null;
  sheetHeight?: number | string | null;
  ups?: number | null;
  layoutCode?: string | null;
  imageUrl?: string | null;
  onUploadImage?: (activeLayout?: any) => void;
  className?: string;
}

export function POSVGMarquette({
  printLayouts = [],
  sheetWidth = 42.5,
  sheetHeight = 67,
  ups = 24,
  layoutCode = "BÌNH CHUẨN",
  imageUrl,
  onUploadImage,
  className = "",
}: POSVGMarquetteProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [showLightbox, setShowLightbox] = useState(false);

  // Normalize list of layouts
  const hasMultipleLayouts = printLayouts && printLayouts.length > 0;
  const safeActiveIndex = Math.min(activeIndex, Math.max(0, printLayouts.length - 1));
  const activeLayout = hasMultipleLayouts ? printLayouts[safeActiveIndex] : null;

  // Active Layout properties
  const activeLayoutCode = activeLayout?.layoutCode || activeLayout?.name || layoutCode || "Bài bình in";
  const activeImageUrl = (activeLayout?.fileUrl ? getUploadUrl(activeLayout.fileUrl) : null)
    || (imageUrl ? getUploadUrl(imageUrl) : null);

  // Parse dimensions for active layout
  const rawSheetSize = activeLayout?.sheetSize || `${sheetWidth}x${sheetHeight}`;
  const sizeParts = rawSheetSize.split("x");
  const numWidth = parseFloat(sizeParts[0]) || 42.5;
  const numHeight = parseFloat(sizeParts[1]) || 67;
  const numUps = activeLayout?.ups || ups || 1;

  const [viewMode, setViewMode] = useState<"vector" | "image">(activeImageUrl ? "image" : "vector");

  useEffect(() => {
    if (activeImageUrl) {
      setViewMode("image");
    } else {
      setViewMode("vector");
    }
  }, [activeImageUrl, safeActiveIndex]);

  // Calculate grid layout for UPS (e.g. 24 -> 4 rows x 6 cols)
  const cols = Math.max(1, Math.ceil(Math.sqrt(numUps * (numHeight / (numWidth || 1)))));
  const rows = Math.max(1, Math.ceil(numUps / cols));

  return (
    <div className={`flex flex-col h-full rounded-lg border border-slate-200 bg-white p-3 shadow-sm ${className}`}>
      {/* Header bar of Marquette */}
      <div className="flex flex-col gap-1.5 pb-2 mb-2 border-b border-slate-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 min-w-0">
            <FileCode className="h-4 w-4 text-indigo-600 shrink-0" />
            <span className="text-xs font-bold text-slate-800 truncate" title={activeLayoutCode}>
              {activeLayoutCode}
            </span>
            <Badge variant="outline" className="text-[10px] px-1 py-0 h-4 bg-indigo-50 text-indigo-700 border-indigo-200 font-mono">
              {numUps} con
            </Badge>
          </div>

          <div className="flex items-center gap-1 shrink-0">
            {activeImageUrl && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 text-slate-500 hover:text-indigo-600"
                title={viewMode === "image" ? "Xem vector CAD" : "Xem ảnh chụp"}
                onClick={() => setViewMode(viewMode === "image" ? "vector" : "image")}
              >
                <ImageIcon className="h-3.5 w-3.5" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 text-slate-500 hover:text-indigo-600"
              title="Xem lớn"
              onClick={() => setShowLightbox(true)}
            >
              <Maximize2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>

        {/* Multi-Layout Pill Switcher (When N > 1) */}
        {printLayouts && printLayouts.length > 1 && (
          <div className="flex items-center gap-1 overflow-x-auto no-scrollbar pt-0.5">
            {printLayouts.map((l: any, idx: number) => {
              const isSelected = idx === safeActiveIndex;
              const hasImg = !!l.fileUrl;
              return (
                <button
                  key={l.id || idx}
                  type="button"
                  onClick={() => setActiveIndex(idx)}
                  className={`flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full font-medium transition-all shrink-0 ${
                    isSelected
                      ? "bg-indigo-600 text-white font-semibold shadow-xs"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                  title={l.name || `Bài bình ${idx + 1}`}
                >
                  <span>{l.name ? l.name.slice(0, 16) : `Bài ${idx + 1}`}</span>
                  {hasImg && (
                    <span className={`w-1.5 h-1.5 rounded-full ${isSelected ? "bg-emerald-300" : "bg-emerald-500"}`} />
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Marquette Body */}
      <div className="relative flex-1 min-h-[140px] w-full flex items-center justify-center bg-slate-50/70 rounded-md border border-dashed border-slate-200 p-2 overflow-hidden select-none">
        {viewMode === "image" && activeImageUrl ? (
          <img
            src={activeImageUrl}
            alt="Bài bình in"
            className="w-full h-full max-h-[160px] object-contain cursor-pointer hover:scale-105 transition-transform"
            onClick={() => setShowLightbox(true)}
          />
        ) : (
          /* SVG Blueprint Render */
          <div className="w-full h-full flex flex-col items-center justify-center">
            <svg
              viewBox="0 0 280 160"
              className="w-full h-full max-h-[160px] filter drop-shadow-sm"
              xmlns="http://www.w3.org/2000/svg"
            >
              <defs>
                <pattern id="po-grid" width="10" height="10" patternUnits="userSpaceOnUse">
                  <path d="M 10 0 L 0 0 0 10" fill="none" stroke="#e2e8f0" strokeWidth="0.5" />
                </pattern>
              </defs>
              <rect width="280" height="160" fill="url(#po-grid)" rx="4" />

              {/* Tờ in mộc (Outer Sheet) */}
              <rect
                x="25"
                y="15"
                width="230"
                height="130"
                fill="#ffffff"
                stroke="#6366f1"
                strokeWidth="1.5"
                rx="2"
              />

              {/* Nhíp in (Gripper Edge) */}
              <line
                x1="25"
                y1="137"
                x2="255"
                y2="137"
                stroke="#f43f5e"
                strokeWidth="1.5"
                strokeDasharray="3 2"
              />
              <text x="140" y="143" fill="#f43f5e" fontSize="7" fontWeight="bold" textAnchor="middle">
                Nhíp in (10mm)
              </text>

              {/* Vùng in khả dụng (Printable area) */}
              <rect
                x="32"
                y="20"
                width="216"
                height="112"
                fill="#f8fafc"
                stroke="#cbd5e1"
                strokeWidth="1"
                strokeDasharray="2 2"
              />

              {/* UPS Matrix Cells */}
              {Array.from({ length: Math.min(numUps, 48) }).map((_, i) => {
                const r = Math.floor(i / cols);
                const c = i % cols;
                const cellW = (216 - (cols + 1) * 2) / (cols || 1);
                const cellH = (112 - (rows + 1) * 2) / (rows || 1);
                const x = 34 + c * (cellW + 2);
                const y = 22 + r * (cellH + 2);
                return (
                  <g key={i}>
                    <rect
                      x={x}
                      y={y}
                      width={cellW}
                      height={cellH}
                      fill="#e0e7ff"
                      stroke="#818cf8"
                      strokeWidth="0.8"
                      rx="1"
                    />
                    <text
                      x={x + cellW / 2}
                      y={y + cellH / 2 + 2}
                      fill="#3730a3"
                      fontSize="6"
                      fontFamily="monospace"
                      fontWeight="bold"
                      textAnchor="middle"
                    >
                      {i + 1}
                    </text>
                  </g>
                );
              })}

              {/* Dimensions Indicator */}
              <text x="140" y="11" fill="#475569" fontSize="6.5" fontWeight="bold" textAnchor="middle">
                Dài: {numHeight} cm
              </text>
              <text x="14" y="80" fill="#475569" fontSize="6.5" fontWeight="bold" textAnchor="middle" transform="rotate(-90 14 80)">
                Rộng: {numWidth} cm
              </text>
            </svg>
          </div>
        )}
      </div>

      {/* Footer info & Upload trigger */}
      <div className="flex items-center justify-between pt-2 mt-2 border-t border-slate-100 text-xs">
        <span className="text-[11px] text-slate-500 font-medium">
          Khổ: {numWidth} × {numHeight} cm
        </span>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-6 text-[11px] px-2 text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 font-semibold"
          onClick={() => onUploadImage && onUploadImage(activeLayout)}
        >
          <Upload className="h-3 w-3 mr-1" />
          {activeImageUrl ? "Đổi ảnh" : "Tải ảnh CAD"}
        </Button>
      </div>

      {/* Lightbox Modal */}
      <Dialog open={showLightbox} onOpenChange={setShowLightbox}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileCode className="h-5 w-5 text-indigo-600" />
              Bản vẽ Bài Bình In — {activeLayoutCode} ({numUps} con/bài)
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 flex flex-col items-center justify-center min-h-[350px] bg-slate-50 rounded-lg p-4 border border-slate-200">
            {activeImageUrl ? (
              <img
                src={activeImageUrl}
                alt="Bài bình in"
                className="max-w-full max-h-[500px] object-contain rounded drop-shadow-md"
              />
            ) : (
              <div className="w-full max-w-xl">
                <svg viewBox="0 0 280 160" className="w-full h-auto filter drop-shadow-md">
                  <rect width="280" height="160" fill="#f8fafc" rx="6" />
                  <rect x="25" y="15" width="230" height="130" fill="#ffffff" stroke="#6366f1" strokeWidth="2" rx="3" />
                  <line x1="25" y1="137" x2="255" y2="137" stroke="#f43f5e" strokeWidth="2" strokeDasharray="4 2" />
                  <text x="140" y="143" fill="#f43f5e" fontSize="7" fontWeight="bold" textAnchor="middle">
                    Đường nhíp in (10mm kẹp máy)
                  </text>
                  <rect x="32" y="20" width="216" height="112" fill="#eff6ff" stroke="#93c5fd" strokeWidth="1" strokeDasharray="3 3" />
                  {Array.from({ length: Math.min(numUps, 48) }).map((_, i) => {
                    const r = Math.floor(i / cols);
                    const c = i % cols;
                    const cellW = (216 - (cols + 1) * 2) / (cols || 1);
                    const cellH = (112 - (rows + 1) * 2) / (rows || 1);
                    const x = 34 + c * (cellW + 2);
                    const y = 22 + r * (cellH + 2);
                    return (
                      <g key={i}>
                        <rect x={x} y={y} width={cellW} height={cellH} fill="#c7d2fe" stroke="#6366f1" strokeWidth="1" rx="1" />
                        <text x={x + cellW / 2} y={y + cellH / 2 + 2} fill="#312e81" fontSize="7" fontFamily="monospace" fontWeight="bold" textAnchor="middle">
                          {i + 1}
                        </text>
                      </g>
                    );
                  })}
                  <text x="140" y="11" fill="#334155" fontSize="8" fontWeight="bold" textAnchor="middle">
                    Chiều dài tờ in: {numHeight} cm
                  </text>
                  <text x="12" y="80" fill="#334155" fontSize="8" fontWeight="bold" textAnchor="middle" transform="rotate(-90 12 80)">
                    Chiều rộng tờ in: {numWidth} cm
                  </text>
                </svg>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
